
import { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import { findQrSession, markQrSessionUsed } from '../../db/qrSessionRepository';

export default function QRLinkPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [status, setStatus] = useState<'loading' | 'no-token' | 'expired' | 'used' | 'not-found' | 'success' | 'guest'>('loading');
  const [error, setError] = useState('');
  const processedRef = useRef(false);

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;
    handleQRLink();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleQRLink = async () => {
    const token = searchParams.get('token');

    if (!token) {
      setStatus('no-token');
      return;
    }

    // 1. QR 세션 유효성 확인
    try {
      const session = await findQrSession(token);

      if (!session) {
        setStatus('not-found');
        return;
      }

      if (session.is_used) {
        setStatus('used');
        return;
      }

      const expiresAt = new Date(session.expires_at);
      if (expiresAt < new Date()) {
        setStatus('expired');
        return;
      }

      // 2. QR 세션에 auth 토큰이 있으면 자동 로그인 (로그인된 PC에서 생성한 QR)
      if (session.auth_access_token && session.auth_refresh_token) {
        const { data: authData, error: authError } = await supabase.auth.setSession({
          access_token: session.auth_access_token,
          refresh_token: session.auth_refresh_token,
        });

        if (authError) {
          // setSession 실패 이유를 error state에 기록 (디버깅용)
          setError(`[auto-login] ${authError.message}`);
        }

        if (!authError && authData.user) {
          await markQrSessionUsed(token, authData.user.id);
          localStorage.setItem('qr_auto_play', 'true');
          localStorage.setItem('qr_session_token', token);
          localStorage.setItem('qr_user_id', authData.user.id);
          setStatus('success');
          setTimeout(() => {
            navigate('/qr-select');
          }, 1500);
          return;
        }
      }

      // 3. 로그인 여부 확인
      const { data: { user } } = await supabase.auth.getUser();

      if (user) {
        // 이미 로그인된 사용자 → 세션 연결 처리
        await markQrSessionUsed(token, user.id);

        // 자동 재생 플래그 설정
        localStorage.setItem('qr_auto_play', 'true');
        localStorage.setItem('qr_session_token', token);
        localStorage.setItem('qr_user_id', user.id);

        setStatus('success');
        setTimeout(() => {
          navigate('/qr-select');
        }, 1500);
      } else {
        // 비로그인 사용자 → 회원가입 안내
        // 토큰을 저장해두어 회원가입/로그인 후 자동 연결할 수 있도록
        localStorage.setItem('pending_qr_token', token);
        setStatus('guest');
      }
    } catch {
      setStatus('not-found');
    }
  };

  // 로딩 중
  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-violet-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">{t('qr.checking')}</p>
        </div>
      </div>
    );
  }

  // 로그인 성공
  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 to-purple-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-sm w-full text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="ri-check-line text-5xl text-green-600"></i>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('qr.login_success')}</h1>
          <p className="text-gray-600">{t('qr.redirecting_remote')}</p>
        </div>
      </div>
    );
  }

  // 비로그인 사용자 → 회원가입 안내
  if (status === 'guest') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 to-purple-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden max-w-sm w-full">
          {/* 상단 헤더 */}
          <div className="bg-gradient-to-r from-violet-500 to-purple-500 px-6 py-8 text-white text-center">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <i className="ri-qr-scan-2-line text-4xl"></i>
            </div>
            <h1 className="text-2xl font-bold mb-2">{t('qr.welcome')}</h1>
            <p className="text-violet-100 text-sm">{t('qr.welcome_subtitle')}</p>
          </div>

          <div className="p-6">
            {/* 디버그: setSession 에러 표시 */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4 text-xs text-red-700 break-all">
                {error}
              </div>
            )}
            {/* 안내 메시지 */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
              <div className="flex gap-3">
                <i className="ri-information-line text-xl text-amber-600 flex-shrink-0 mt-0.5"></i>
                <div className="text-sm text-amber-800">
                  <p className="font-medium mb-1">{t('qr.login_required')}</p>
                  <p className="text-amber-700">{t('qr.login_required_desc')}</p>
                </div>
              </div>
            </div>

            {/* 기능 소개 */}
            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-smartphone-line text-lg text-violet-600"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{t('qr.feature_remote')}</p>
                  <p className="text-xs text-gray-500">{t('qr.feature_remote_desc')}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-focus-3-line text-lg text-teal-600"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{t('qr.feature_laser')}</p>
                  <p className="text-xs text-gray-500">{t('qr.feature_laser_desc')}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-timer-line text-lg text-amber-600"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{t('qr.feature_timer')}</p>
                  <p className="text-xs text-gray-500">{t('qr.feature_timer_desc')}</p>
                </div>
              </div>
            </div>

            {/* 버튼들 */}
            <div className="space-y-3">
              <Link
                to="/signup"
                className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-violet-500 to-purple-500 text-white py-4 rounded-xl font-medium hover:from-violet-600 hover:to-purple-600 transition-all whitespace-nowrap"
              >
                <i className="ri-user-add-line text-xl"></i>
                {t('qr.signup_action')}
              </Link>

              <Link
                to="/login"
                className="w-full flex items-center justify-center gap-2 bg-gray-100 text-gray-700 py-3 rounded-xl font-medium hover:bg-gray-200 transition-all whitespace-nowrap"
              >
                <i className="ri-login-box-line"></i>
                {t('qr.has_account_login')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 에러 상태들
  const errorConfig: Record<string, { icon: string; title: string; desc: string }> = {
    'no-token': { icon: 'ri-error-warning-line', title: t('qr.error_invalid'), desc: t('qr.error_invalid_desc') },
    'expired': { icon: 'ri-time-line', title: t('qr.error_expired'), desc: t('qr.error_expired_desc') },
    'used': { icon: 'ri-checkbox-circle-line', title: t('qr.error_used'), desc: t('qr.error_used_desc') },
    'not-found': { icon: 'ri-search-line', title: t('qr.error_not_found'), desc: t('qr.error_not_found_desc') },
  };

  const config = errorConfig[status] || errorConfig['not-found'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 to-purple-50 flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-sm w-full text-center">
        <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <i className={`${config.icon} text-4xl text-red-500`}></i>
        </div>
        <h1 className="text-xl font-bold text-gray-900 mb-2">{config.title}</h1>
        <p className="text-gray-600 text-sm mb-6">{config.desc}</p>
        {error && <p className="text-red-500 text-sm mb-4">{error}</p>}
        <Link
          to="/"
          className="w-full flex items-center justify-center gap-2 bg-gray-100 text-gray-700 py-3 rounded-xl font-medium hover:bg-gray-200 transition-all whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i>
          {t('qr.go_home')}
        </Link>
      </div>
    </div>
  );
}
